import React, { useState } from "react";
import ParentHeader from "../ParentHeader/ParentHeader";
import SiteHeader from "../../Site/SiteHeader";
import SiteFooter from "../../Site/SiteFooter";
import favIcon from "../../Assets/favicon.ico";
import { useMaterialSymbols } from "../../Teacher/Oireachtas/components";
import { usePageMeta } from "../../Site/useSiteMotion";
import { useCompetitionCounts } from "../../Components/EntryCard/entryCardModel";
import {
  addressLine,
  dayRange,
  directionsUrl,
  eventDayOf,
  feeLines,
  flagOf,
  headline,
  initialsOf,
  keyDates,
  linkParts,
  longDay,
  midDay,
  money,
  paragraphs,
  peopleGroups,
  placeLine,
} from "./eventSiteModel";
import "../../Site/site.css";
import "./eventSite.css";

// The feis's own page on Feis.link (MC2 ecbc86f7, direction B). It reads
// like the event's website, in the public site's language (src/Site): a
// green hero with the crest, the one thing that matters now and the main
// action; a sticky row of sections; then everything a parent asks before
// and after entering - what my dancer can enter and for how much, when
// things happen, where it is and where to stay, who is judging, who to ask.
// A section the organiser left empty is not shown.

const Icon = ({ name }) => <span className="msr" aria-hidden="true">{name}</span>;
const openUrl = (url) => url && window.open(url, "_blank", "noopener,noreferrer");

const Linked = ({ text }) =>
  linkParts(text).map((p, i) =>
    p.href ? (
      <a key={i} href={p.href} target={p.href.startsWith("mailto:") ? undefined : "_blank"} rel="noopener noreferrer" className="es-a">
        {p.text}
      </a>
    ) : (
      <React.Fragment key={i}>{p.text}</React.Fragment>
    )
  );

const Section = ({ id, title, sub, children, wide }) => (
  <section id={id} className={`es-sec${wide ? " wide" : ""}`}>
    <div className="es-sec-head">
      <h2>{title}</h2>
      {sub && <p>{sub}</p>}
    </div>
    {children}
  </section>
);

const Hotel = ({ hotel }) => {
  const [more, setMore] = useState(false);
  const address = (hotel?.address || "").trim();
  const about = (hotel?.description || "").trim();
  const directions = directionsUrl(hotel, address);
  return (
    <div className="es-card es-hotel">
      {hotel?.photo && <img className="es-hotel-photo" src={hotel.photo} alt="" />}
      <div className="es-hotel-body">
        <h3>{hotel?.name}</h3>
        {address && <span className="meta">{address}</span>}
        {about && (
          <>
            <p className={`es-hotel-about${more ? " open" : ""}`}>
              <Linked text={about} />
            </p>
            {about.length > 160 && (
              <button type="button" className="es-textbtn" onClick={() => setMore(!more)}>
                {more ? "Show less" : "Room block details"}
              </button>
            )}
          </>
        )}
        <div className="es-acts">
          {hotel?.url && (
            <button type="button" className="sbtn ghost sm" onClick={() => openUrl(hotel.url)}>
              <Icon name="open_in_new" /> Book
            </button>
          )}
          {directions && (
            <button type="button" className="sbtn ghost sm" onClick={() => openUrl(directions)}>
              <Icon name="directions" /> Directions
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const EventSite = ({
  event,
  kind = "feis",
  state,
  entered = false,
  dancerCount = 0,
  actions = [],
  collaborators,
  dancers,
  grades,
  card,
  counts: countsIn,
  onBack,
  backLabel = "Events",
}) => {
  useMaterialSymbols();
  const signedIn = !!localStorage.getItem("accessToken");
  const eventId = event?._id;
  // once the organiser opens the competition counts, how many entries there
  // are (the entered page reads them for its card and passes them in)
  const ownCounts = useCompetitionCounts(eventId, kind === "feis" && signedIn && countsIn === undefined);
  const counts = countsIn === undefined ? ownCounts : countsIn;

  const head = headline(event, { state, entered, dancerCount, kind });
  const dates = keyDates(event, { entered, kind });
  const fees = kind === "feis" ? feeLines(event) : [];
  const people = peopleGroups(collaborators, kind);
  // judges and musicians as portraits under About; sponsors as logos further down
  const judges = people.filter((g) => g.title !== "Sponsors");
  // titled for the first group there is (Adjudicators, or Musicians when no adjudicators are listed)
  const peopleTitle = judges[0]?.title || (kind === "grade" ? "Examiners" : "Adjudicators");
  const sponsors = people.find((g) => g.title === "Sponsors")?.people || [];
  const hotels = (event?.accomodations || []).filter((h) => h?.name);
  const about = paragraphs(event?.description);
  const address = addressLine(event);
  const place = placeLine(event);
  const directions = directionsUrl(event, address || event?.venue_name);
  const contact = (event?.contact_email || "").trim();
  const totalEntered = counts ? [...counts.values()].reduce((a, b) => a + b, 0) : null;
  const noun = { feis: "Feis", grade: "Grade exam", oireachtas: "Oireachtas" }[kind];
  const eventDay = eventDayOf(event);
  // a multi-day Oireachtas reads "November 6–8, 2026"
  const when = kind === "oireachtas" && event?.eventDays?.length > 1 ? dayRange(event.eventDays) : eventDay && longDay(eventDay);

  usePageMeta(
    event?.name ? `${event.name} - Feis.link` : "Feis.link",
    [noun, when, event?.venue_name, place].filter(Boolean).join(" · ")
  );

  const nav = [
    dancers && ["you", entered ? "Your entries" : "Your dancers"],
    about.length > 0 && ["about", "About"],
    (address || event?.venue_name) && ["venue", "Venue"],
    judges.length > 0 && ["people", peopleTitle],
    grades?.length > 0 && ["grades", "Grades"],
    fees.length > 0 && ["fees", "Fees"],
    dates.length > 0 && ["dates", "Dates"],
    hotels.length > 0 && ["hotels", hotels.length === 1 ? "Hotel" : "Hotels"],
    contact && ["contact", "Contact"],
  ].filter(Boolean);

  // the syllabus and, once posted, the stage schedule sit with the main action
  const allActions = [
    ...actions.filter((a) => !a.primary),
    event?.syllabus_file && { key: "syllabus", icon: "description", label: "Syllabus", onClick: () => openUrl(event.syllabus_file) },
    // the page's own Schedule button gives way to one the event passes in
    event?.schedulePdfUrl && !actions.some((a) => a.key === "schedule") && { key: "schedule", icon: "calendar_month", label: "Schedule", onClick: () => openUrl(event.schedulePdfUrl) },
    ...actions.filter((a) => a.primary),
  ].filter(Boolean);

  const facts = [
    totalEntered >= 20 && { icon: "groups", text: `${totalEntered} entries${state === "result" ? "" : " so far"}` },
  ].filter(Boolean);

  return (
    <div className={`site es${signedIn ? "" : " es-out"}`}>
      {signedIn ? <ParentHeader /> : <SiteHeader active="events" />}

      <section className="hero es-hero">
        <div className="wrap">
          {onBack && (
            <button type="button" className="es-back" onClick={onBack}>
              <Icon name="arrow_back" /> {backLabel}
            </button>
          )}
          <div className="es-id">
            <img className="crest xl" src={event?.logo || favIcon} alt="" />
            <div>
              <span className="eyebrow">
                {noun}
                {event?.region && ` · ${event.region}`}
              </span>
              <h1 className="es-name">{event?.name}</h1>
              <span className="meta es-when">
                {[when, place].filter(Boolean).join(" · ")}
              </span>
            </div>
          </div>

          {/* entered: the family's entry card (the dashboard's card) in place of the status line */}
          {card && <div className="es-card-slot">{card}</div>}
          {!card && head && (
            <div className={`es-now es-now--${head.tone}${head.title || head.detail ? "" : " es-now--bare"}`}>
              {(head.title || head.detail) && (
                <div className="es-now-text">
                  {head.title && <strong>{head.title}</strong>}
                  {head.detail && <span>{head.detail}</span>}
                </div>
              )}
              {allActions.length > 0 && (
                <div className="es-acts">
                  {allActions.map((a) => (
                    <button
                      key={a.key}
                      type="button"
                      className={`sbtn${a.primary ? " big" : " ghost"}${a.key === "register" && state === "late" ? " late" : ""}`}
                      disabled={a.disabled}
                      onClick={a.onClick}
                    >
                      {a.icon && <Icon name={a.icon} />}
                      {a.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {facts.length > 0 && (
            <p className="es-facts">
              {facts.map((f) => (
                <span key={f.icon}>
                  <Icon name={f.icon} />
                  {f.text}
                </span>
              ))}
            </p>
          )}
        </div>
      </section>

      {nav.length > 2 && (
        <nav className="es-nav" aria-label="On this page">
          <div className="wrap">
            {nav.map(([id, label]) => (
              <a key={id} href={`#${id}`} onClick={(e) => {
                e.preventDefault();
                document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
              }}>
                {label}
              </a>
            ))}
          </div>
        </nav>
      )}

      <div className="wrap es-body">
        {dancers && (
          <Section id="you" title={entered ? "Your entries" : "Your dancers"}>
            {dancers}
          </Section>
        )}

        {about.length > 0 && (
          <Section id="about" title="About">
            <div className="es-card es-about">
              {about.map((p, i) => (
                <p key={i}>
                  <Linked text={p} />
                </p>
              ))}
            </div>
          </Section>
        )}

        {(address || event?.venue_name) && (
          <Section id="venue" title="Venue">
            <div className="es-card es-venue">
              <div className="es-venue-text">
                {event?.venue_name && <h3>{event.venue_name}</h3>}
                {address && <span className="meta">{address}</span>}
                {directions && (
                  <div className="es-acts">
                    <button type="button" className="sbtn" onClick={() => openUrl(directions)}>
                      <Icon name="directions" /> Directions
                    </button>
                  </div>
                )}
              </div>
            </div>
          </Section>
        )}

        {judges.length > 0 && (
          <Section id="people" title={peopleTitle}>
            {judges.map((g, gi) => (
              <div className="es-people-group" key={g.title}>
                {/* the section is titled for the first group; any later group (musicians) gets its own label */}
                {gi > 0 && <span className="eyebrow">{g.title}</span>}
                <div className="es-people">
                  {g.people.map((p, i) => (
                    <figure className={`es-portrait${p.photo ? "" : " no-photo"}`} key={p.id || i}>
                      {p.photo ? (
                        <img src={p.photo} alt="" loading="lazy" />
                      ) : (
                        <span className="es-initials" aria-hidden="true">
                          {initialsOf(p.name)}
                        </span>
                      )}
                      <figcaption>
                        <span className="es-person-name">{p.name}</span>
                        {p.location && (
                          <span className="es-person-where">
                            {flagOf(p.location) && <span className="es-flag" aria-hidden="true">{flagOf(p.location)}</span>}
                            {p.location}
                          </span>
                        )}
                      </figcaption>
                    </figure>
                  ))}
                </div>
              </div>
            ))}
          </Section>
        )}

        {grades?.length > 0 && (
          <Section id="grades" title="Grades" sub="Each grade a dancer takes, and its fee.">
            <div className="es-card">
              <ul className="es-list">
                {grades.map((g, i) => (
                  <li key={g._id || i}>
                    <span>{g.grade_name}</span>
                    <b>{money(g.grade_fee)}</b>
                  </li>
                ))}
              </ul>
            </div>
          </Section>
        )}

        <div className="es-two">
          {fees.length > 0 && (
            <Section id="fees" title="Fees">
              <div className="es-card">
                <ul className="es-list">
                  {fees.map((f) => (
                    <li key={f.key}>
                      <span>
                        {f.label}
                        {f.note && <small>{f.note}</small>}
                      </span>
                      <b>{f.value}</b>
                    </li>
                  ))}
                </ul>
              </div>
            </Section>
          )}

          {dates.length > 0 && (
            <Section id="dates" title="Dates">
              <div className="es-card">
                <ol className="es-timeline">
                  {dates.map((d) => (
                    <li key={d.key} className={`${d.past ? "past" : ""}${d.main ? " main" : ""}`}>
                      <span className="es-dot" aria-hidden="true" />
                      <span className="es-t-label">{d.label}</span>
                      <span className="es-t-day">{midDay(d.date)}</span>
                    </li>
                  ))}
                </ol>
                {!event?.schedulePdfUrl && !dates.some((d) => d.main && d.past) && (
                  <p className="es-fine">The stage schedule is posted here closer to the day.</p>
                )}
              </div>
            </Section>
          )}
        </div>

        {hotels.length > 0 && (
          <Section id="hotels" title={hotels.length === 1 ? "Where to stay" : "Where to stay"} sub="Room blocks the organiser has arranged.">
            <div className="es-hotels">
              {hotels.map((h, i) => (
                <Hotel key={h._id || i} hotel={h} />
              ))}
            </div>
          </Section>
        )}

        {sponsors.length > 0 && (
          <Section id="sponsors" title="Sponsors" sub="Thank you to the businesses supporting this event.">
            <div className="es-sponsors">
              {sponsors.map((p, i) => (
                <div className="es-sponsor" key={p.id || i}>
                  {p.photo ? <img src={p.photo} alt="" loading="lazy" /> : <span className="es-initials">{initialsOf(p.name)}</span>}
                  <span>{p.name}</span>
                </div>
              ))}
            </div>
          </Section>
        )}

        {contact && (
          <Section id="contact" title="Questions?">
            <div className="es-card es-contact">
              <p>The organiser answers questions about this {kind === "grade" ? "exam" : kind === "oireachtas" ? "Oireachtas" : "feis"}: rules, the schedule, refunds.</p>
              <a className="sbtn ghost" href={`mailto:${contact}?subject=${encodeURIComponent(event?.name || "")}`}>
                <Icon name="mail" /> Email the organiser
              </a>
            </div>
          </Section>
        )}
      </div>

      {!signedIn && <SiteFooter />}
    </div>
  );
};

export default EventSite;
